// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/lib/unreadBadge.ts
// 🔧 PURPOSE: I messaggi della reception non ancora letti, per chi sta
//             FUORI dalla Home: il numerino su "Soggiorno" nella barra in
//             basso (Layout).
//             Il numero lo conosce gia' HomeSessionHost (useUnreadMessages,
//             il suo controllo al minuto): qui lo si ripete e basta, senza
//             richieste in piu' e senza toccare la chat. Vale solo per il
//             token che l'ha scritto: un altro accesso non eredita il numero
//             e, usciti, non si vede. Fuori dalla Home resta l'ultimo numero
//             noto; quando la chat segna tutto come letto (anche quella di
//             MyStay, evento `nva_chat_read`) torna a zero subito.
// ============================================

import { useSyncExternalStore } from 'react';

/** L'evento del drawer della chat dopo la lettura segnata sul server. */
export const CHAT_READ_EVENT = 'nva_chat_read';

interface UnreadState {
  owner: string;
  count: number;
}

let state: UnreadState = { owner: '', count: 0 };
const listeners = new Set<() => void>();

function emit(next: UnreadState): void {
  if (next.owner === state.owner && next.count === state.count) return;
  state = next;
  listeners.forEach((fn) => fn());
}

export const unreadBadgeStore = {
  /** Il numero dei non letti per il token `owner` (lo scrive HomeSessionHost). */
  publish(owner: string, count: number): void {
    const n = Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
    emit({ owner, count: n });
  },
  /** Letti tutti: il numerino sparisce. */
  clear(): void {
    if (state.count !== 0) emit({ owner: state.owner, count: 0 });
  },
};

const onChatRead = () => unreadBadgeStore.clear();

const subscribe = (fn: () => void): (() => void) => {
  listeners.add(fn);
  if (listeners.size === 1 && typeof window !== 'undefined') {
    window.addEventListener(CHAT_READ_EVENT, onChatRead);
  }
  return () => {
    listeners.delete(fn);
    if (listeners.size === 0 && typeof window !== 'undefined') {
      window.removeEventListener(CHAT_READ_EVENT, onChatRead);
    }
  };
};

const getSnapshot = (): UnreadState => state;

/** I non letti della sessione con questo token (0 senza token o per un altro token). */
export function useUnreadBadge(owner: string | null | undefined): number {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return owner && snapshot.owner === owner ? snapshot.count : 0;
}
